<template>
  <!-- Pegado a la lista de «Servicios» de la ficha de inspector, no en un rótulo al principio de
       la pantalla: quien mira qué puede hacer este inspector tiene que ver ahí mismo que parte de
       esa lista no habilita nada (§9 del CLAUDE.md del orquestador). Y si no hay nada que decir no
       se dibuja nada: una ficha limpia no enseña aviso. -->
  <div v-if="hayAlgoQueDecir" class="col-100 huerfanos-aviso">
    <div v-if="errorCarga" class="huerfanos-nota-gris">{{ $t('empleados.huerfanos.noComprobado') }}</div>
    <template v-else>
      <div v-if="huerfanos.length" class="huerfanos-cuerpo">
        <div class="huerfanos-titulo">
          ⚠️
          {{ huerfanos.length == 1
             ? $t('empleados.huerfanos.tituloUno')
             : $t('empleados.huerfanos.tituloVarios', { n: huerfanos.length }) }}
        </div>
        <!-- El por qué y el quién van juntos y aquí, no en un tooltip: el aviso tiene que poder
             accionarse leyéndolo. -->
        <div class="huerfanos-motivo">{{ $t('empleados.huerfanos.porque') }}</div>
        <div class="huerfanos-motivo">{{ $t('empleados.huerfanos.quien') }}</div>
        <ul class="huerfanos-lista">
          <li v-for="(huerfano, i) in huerfanos" :key="'hu-' + i">
            <b>{{ huerfano.codigo || $t('empleados.huerfanos.sinCodigo') }}</b>
            <span v-if="huerfano.nombre"> — {{ huerfano.nombre }}</span>
          </li>
        </ul>
      </div>
      <!-- Los repetidos no son huérfanos y no se mezclan con ellos: ese servicio sí está en el
           catálogo, lo que sobra es la entrada de más. -->
      <div v-if="repetidos.length" class="huerfanos-nota-gris">
        {{ repetidos.length == 1
           ? $t('empleados.huerfanos.repetidoUno')
           : $t('empleados.huerfanos.repetidosVarios', { n: repetidos.length }) }}
        <b>{{ textoRepetidos }}</b>
      </div>
    </template>
  </div>
</template>
<style scoped>
.huerfanos-aviso {
  margin: 0 8px 8px;
  font-size: 13px;
}
.huerfanos-cuerpo {
  border-left: 3px solid #ff9500;
  padding-left: 8px;
}
.huerfanos-titulo {
  color: #ff9500;
  font-weight: bold;
}
.huerfanos-motivo {
  margin-top: 2px;
}
.huerfanos-lista {
  margin: 4px 0 0;
  padding-left: 18px;
  list-style: disc;
}
.huerfanos-nota-gris {
  color: #8e8e93;
  margin-top: 4px;
}
</style>
<script>
import Inspector from "./../../js/db/Inspector.js";

/**
 * Aviso de códigos huérfanos de la lista de servicios de la ficha de inspector (#329).
 *
 * Solo lee: pregunta por los huérfanos de una ficha y los enseña. Reparar la ficha —quitar la
 * entrada o recuperar el servicio en el catálogo— es una corrección de datos que se decide aparte
 * (#327), así que esta tarjeta no ofrece ningún botón que lo haga.
 *
 * Va dentro de la tarjeta de inspector de la ficha del empleado, inmediatamente después del campo
 * «Servicios», y se recarga cuando se guarda la ficha: lo que se acaba de guardar es justo lo que
 * decide si siguen habiendo huérfanos, así que responder con la foto de antes sería decir que un
 * cambio no ha surtido efecto —o que sí— sin haberlo mirado.
 */
export default {
  props: {
    username: {
      type: String,
      default: null
    }
  },
  data: function() {
    return {
      huerfanos: [],
      repetidos: [],
      // De quién es lo que está pintado ahora mismo. Null mientras se carga: el aviso de la ficha
      // anterior no se queda a la vista sobre los servicios de otro inspector.
      cargadaDe: null,
      // La consulta falló por algo que no es un 403. No se calla: decir nada sería afirmar que la
      // lista está limpia sin haberla podido comprobar.
      errorCarga: false,
      // Sin permiso para esta consulta: ni aviso ni error. Lo mismo que hace la tarjeta de
      // vacaciones, y por lo mismo.
      error403: false,
      // Turno de la última carga pedida. Con A→B→A la respuesta vieja de A vuelve a coincidir con
      // la prop, así que comparar solo el username la aceptaría y pintaría una foto anterior.
      turnoCarga: 0
    };
  },
  computed: {
    hayAlgoQueDecir: function() {
      if (this.error403 || !this.cargadaDe) {
        return false;
      }
      return this.errorCarga || this.huerfanos.length > 0 || this.repetidos.length > 0;
    },
    textoRepetidos: function() {
      return this.repetidos
        .map(function(r) {
          return (r.codigo || r.id || '') + ' (×' + r.veces + ')';
        })
        .join(', ');
    }
  },
  watch: {
    username: function() {
      // El 403 era de la ficha anterior: sin borrarlo, pasar por una que no se puede consultar
      // dejaría el aviso apagado para todas las siguientes.
      this.error403 = false;
      this.cargar();
    }
  },
  created: function() {
    this.cargar();
  },
  methods: {
    es403: function(e) {
      return !!(e && (e.status == 403 || (e.xhr && e.xhr.status == 403)));
    },
    /** Lo llama la ficha después de guardar: la lista de servicios puede haber cambiado. */
    recargar: function() {
      this.cargar();
    },
    cargar: function() {
      const self = this;
      const pedido = self.username;
      const turno = ++self.turnoCarga;
      self.errorCarga = false;
      self.cargadaDe = null;
      self.huerfanos = [];
      self.repetidos = [];
      if (!pedido) {
        return;
      }
      Inspector.serviciosHuerfanos(pedido)
        .then(function(res) {
          if (turno != self.turnoCarga || pedido != self.username) return;
          // Sin ficha de inspector no hay nada que revisar, y eso no es una ficha limpia: no se
          // afirma ni una cosa ni la otra, simplemente no hay aviso.
          if (!res || res.existeFicha === false) {
            self.cargadaDe = pedido;
            return;
          }
          self.huerfanos = res.huerfanos || [];
          self.repetidos = res.repetidos || [];
          self.cargadaDe = pedido;
        })
        .catch(function(e) {
          if (turno != self.turnoCarga || pedido != self.username) return;
          if (self.es403(e)) {
            self.error403 = true;
            return;
          }
          self.errorCarga = true;
          self.cargadaDe = pedido;
        });
    }
  }
};
</script>
